import { isSupabaseConfigured, missingSupabaseConfig } from "./config";
import { supabase } from "./client";

const MANUAL_OPERATIONAL_MZ_RESET_UNITS = 2000;
const MZ_CREDIT_VALUE_ARS = 0.1;
const MZ_TICKET_BASIS_ARS = 100;

const DEFAULT_PLATFORM_BILLING_SETTINGS = Object.freeze({
  singletonKey: "default",
  unitCode: "MZ",
  unitSingular: "Mozzo",
  unitPlural: "Mozzos",
  defaultLowBalanceThreshold: 400,
  defaultNegativeLimit: 0,
});

const DEFAULT_PLATFORM_PRICING_RULES = Object.freeze([
  {
    eventKey: "counter_order_confirmed",
    eventLabel: "Pedido manual confirmado",
    description:
      "Creditos MZ cada $100 de ticket. Se recalcula desde la comision de pedidos manuales del Backoffice.",
    unitsCost: 18,
    enabled: true,
    category: "orders",
    sortOrder: 10,
  },
  {
    eventKey: "menu_qr_order_paid",
    eventLabel: "Pedido Menu QR pagado",
    description:
      "Creditos MZ cada $100 de ticket. Con 8 creditos cada $100 equivale aproximadamente a 0,8% del pedido.",
    unitsCost: 8,
    enabled: true,
    category: "orders",
    sortOrder: 20,
  },
  {
    eventKey: "kiosk_order_paid",
    eventLabel: "Pedido kiosk pagado",
    description:
      "Creditos MZ cada $100 de ticket. Con 5 creditos cada $100 equivale aproximadamente a 0,5% del pedido.",
    unitsCost: 5,
    enabled: true,
    category: "orders",
    sortOrder: 30,
  },
  {
    eventKey: "ai_assistant_order_created",
    eventLabel: "Pedido asistido por IA",
    description:
      "Creditos MZ cada $100 de ticket para pedidos creados o guiados por IA.",
    unitsCost: 18,
    enabled: true,
    category: "ai",
    sortOrder: 40,
  },
  {
    eventKey: "ai_upsell_accepted",
    eventLabel: "Upselling IA aceptado",
    description:
      "Consumo premium cuando una sugerencia inteligente termina aceptada por el comensal.",
    unitsCost: 20,
    enabled: true,
    category: "ai",
    sortOrder: 50,
  },
]);

const COMMISSION_LINKED_RULES = Object.freeze({
  counter_order_confirmed: {
    rateKey: "manualOrderCommissionRate",
    fallbackRate: 0.018,
    description:
      "Creditos MZ cada $100 de ticket. Se recalcula desde la comision de pedidos manuales del Backoffice.",
  },
  menu_qr_order_paid: {
    rateKey: "menuQrCommissionRate",
    fallbackRate: 0.008,
    description:
      "Creditos MZ cada $100 de ticket. Se recalcula desde la comision de Menu QR del Backoffice.",
  },
  kiosk_order_paid: {
    rateKey: "kioskCommissionRate",
    fallbackRate: 0.005,
    description:
      "Creditos MZ cada $100 de ticket. Se recalcula desde la comision de kiosk del Backoffice.",
  },
  ai_assistant_order_created: {
    rateKey: "iaCommissionRate",
    fallbackRate: 0.018,
    description:
      "Creditos MZ cada $100 de ticket. Se recalcula desde la comision de IA del Backoffice.",
  },
});

export const RECOMMENDED_PLATFORM_CREDIT_PACKAGES = Object.freeze([
  {
    packageCode: "inicio",
    packageName: "Pack Inicio",
    description:
      "Entrada para restaurantes que quieren probar kiosk con saldo operativo inicial.",
    unitsAmount: 50000,
    priceArs: 70000,
    enabled: true,
    sortOrder: 10,
  },
  {
    packageCode: "local",
    packageName: "Pack Local",
    description:
      "Pack intermedio para locales que ya usan kiosk de forma sostenida.",
    unitsAmount: 150000,
    priceArs: 200000,
    enabled: true,
    sortOrder: 20,
  },
  {
    packageCode: "alto-flujo",
    packageName: "Pack Alto flujo",
    description:
      "Bolsa grande para restaurantes con venta alta desde kiosk.",
    unitsAmount: 400000,
    priceArs: 500000,
    enabled: true,
    sortOrder: 30,
  },
  {
    packageCode: "empresa",
    packageName: "Pack Empresa",
    description:
      "Pack para empresas o grupos con varias sucursales y alto volumen mensual.",
    unitsAmount: 1000000,
    priceArs: 1150000,
    enabled: true,
    sortOrder: 40,
  },
]);

const DEFAULT_PLATFORM_CREDIT_PACKAGES = RECOMMENDED_PLATFORM_CREDIT_PACKAGES;
const LEGACY_PLATFORM_CREDIT_PACKAGE_CODES = new Set([
  "starter",
  "pro",
  "enterprise",
]);

const buildDefaultCompanyWallet = (companyId = "") => ({
  companyId,
  balanceUnits: MANUAL_OPERATIONAL_MZ_RESET_UNITS,
  lowBalanceThreshold: DEFAULT_PLATFORM_BILLING_SETTINGS.defaultLowBalanceThreshold,
  allowNegativeBalance: false,
  negativeBalanceLimit: DEFAULT_PLATFORM_BILLING_SETTINGS.defaultNegativeLimit,
  lastTransactionAt: "",
});

export const buildDefaultMzBackofficeSnapshot = (companyId = "") => ({
  platformSettings: { ...DEFAULT_PLATFORM_BILLING_SETTINGS },
  companyWallet: buildDefaultCompanyWallet(companyId),
  pricingRules: DEFAULT_PLATFORM_PRICING_RULES.map((rule) => ({ ...rule })),
  creditPackages: DEFAULT_PLATFORM_CREDIT_PACKAGES.map((pkg) => ({ ...pkg })),
  recentTransactions: [],
  recentUsageEvents: [],
});

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toSafeLong = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").trim().replace(",", "."));

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.round(parsed));
};

const toSafeMoney = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").trim().replace(",", "."));

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.round(parsed * 100) / 100);
};

const toSafeRate = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").trim().replace(",", "."));

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(0.25, Math.max(0, Math.round(parsed * 10000) / 10000));
};

export const calculateMzCreditsPerTicketBasisFromRate = (commissionRate) => {
  const normalizedRate = toSafeRate(commissionRate, 0);
  const commissionAmountPerBasis = MZ_TICKET_BASIS_ARS * normalizedRate;

  return Math.max(
    0,
    Math.round(commissionAmountPerBasis / MZ_CREDIT_VALUE_ARS)
  );
};

export const buildCommissionLinkedPricingRules = (
  currentRules = [],
  commissionRates = {}
) => {
  const currentByKey = new Map(
    (Array.isArray(currentRules) ? currentRules : []).map((rule) => [
      toSafeText(rule?.eventKey || rule?.event_key, 80),
      rule,
    ])
  );

  return DEFAULT_PLATFORM_PRICING_RULES.map((defaultRule) => {
    const currentRule = currentByKey.get(defaultRule.eventKey) || {};
    const linkedRule = COMMISSION_LINKED_RULES[defaultRule.eventKey];
    const nextUnitsCost = linkedRule
      ? calculateMzCreditsPerTicketBasisFromRate(
          commissionRates?.[linkedRule.rateKey] ?? linkedRule.fallbackRate
        )
      : toSafeLong(currentRule.unitsCost ?? currentRule.units_cost, defaultRule.unitsCost);

    return {
      ...defaultRule,
      ...currentRule,
      eventKey: defaultRule.eventKey,
      eventLabel: currentRule.eventLabel || currentRule.event_label || defaultRule.eventLabel,
      description:
        linkedRule?.description ||
        currentRule.description ||
        defaultRule.description,
      unitsCost: nextUnitsCost,
      enabled:
        linkedRule ? true : currentRule.enabled !== undefined ? currentRule.enabled : defaultRule.enabled,
      category: currentRule.category || defaultRule.category,
      sortOrder: toSafeLong(
        currentRule.sortOrder ?? currentRule.sort_order,
        defaultRule.sortOrder
      ),
    };
  });
};

const toSafeBoolean = (value) => value === true;

const normalizeError = (error, fallbackMessage) => {
  if (!error) {
    return new Error(fallbackMessage);
  }

  return new Error(error.message || fallbackMessage);
};

const isMissingMzBillingStructureError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42p01" ||
    error?.code === "42703" ||
    message.includes("platform_billing_settings") ||
    message.includes("platform_pricing_rules") ||
    message.includes("platform_credit_packages") ||
    message.includes("company_wallets") ||
    message.includes("company_wallet_transactions") ||
    message.includes("company_usage_events")
  );
};

const normalizePlatformBillingSettings = (row = {}) => ({
  singletonKey:
    toSafeText(row.singleton_key || row.singletonKey, 40) ||
    DEFAULT_PLATFORM_BILLING_SETTINGS.singletonKey,
  unitCode:
    toSafeText(row.unit_code || row.unitCode, 16) ||
    DEFAULT_PLATFORM_BILLING_SETTINGS.unitCode,
  unitSingular:
    toSafeText(row.unit_singular || row.unitSingular, 40) ||
    DEFAULT_PLATFORM_BILLING_SETTINGS.unitSingular,
  unitPlural:
    toSafeText(row.unit_plural || row.unitPlural, 40) ||
    DEFAULT_PLATFORM_BILLING_SETTINGS.unitPlural,
  defaultLowBalanceThreshold: toSafeLong(
    row.default_low_balance_threshold ?? row.defaultLowBalanceThreshold,
    DEFAULT_PLATFORM_BILLING_SETTINGS.defaultLowBalanceThreshold
  ),
  defaultNegativeLimit: toSafeLong(
    row.default_negative_limit ?? row.defaultNegativeLimit,
    DEFAULT_PLATFORM_BILLING_SETTINGS.defaultNegativeLimit
  ),
});

const normalizeCompanyWallet = (row = {}, platformSettings = {}) => ({
  companyId: toSafeText(row.company_id || row.companyId, 80),
  balanceUnits: toSafeLong(row.balance_units ?? row.balanceUnits, 0),
  lowBalanceThreshold: toSafeLong(
    row.low_balance_threshold ?? row.lowBalanceThreshold,
    platformSettings.defaultLowBalanceThreshold ??
      DEFAULT_PLATFORM_BILLING_SETTINGS.defaultLowBalanceThreshold
  ),
  allowNegativeBalance: toSafeBoolean(
    row.allow_negative_balance ?? row.allowNegativeBalance
  ),
  negativeBalanceLimit: toSafeLong(
    row.negative_balance_limit ?? row.negativeBalanceLimit,
    platformSettings.defaultNegativeLimit ??
      DEFAULT_PLATFORM_BILLING_SETTINGS.defaultNegativeLimit
  ),
  lastTransactionAt: toSafeText(
    row.last_transaction_at || row.lastTransactionAt,
    80
  ),
});

const normalizePricingRule = (row = {}, fallbackSortOrder = 0) => ({
  id: toSafeText(row.id, 80),
  eventKey: toSafeText(row.event_key || row.eventKey, 80),
  eventLabel: toSafeText(row.event_label || row.eventLabel, 120),
  description: toSafeText(row.description, 280),
  unitsCost: toSafeLong(row.units_cost ?? row.unitsCost, 0),
  enabled: row.enabled !== false,
  category: toSafeText(row.category, 40) || "orders",
  sortOrder: toSafeLong(row.sort_order ?? row.sortOrder, fallbackSortOrder),
});

const normalizeCreditPackage = (row = {}, fallbackSortOrder = 0) => ({
  id: toSafeText(row.id, 80),
  packageCode: toSafeText(row.package_code || row.packageCode, 80),
  packageName: toSafeText(row.package_name || row.packageName, 120),
  description: toSafeText(row.description, 280),
  unitsAmount: toSafeLong(row.units_amount ?? row.unitsAmount, 0),
  priceArs: toSafeMoney(row.price_ars ?? row.priceArs, 0),
  enabled: row.enabled !== false,
  sortOrder: toSafeLong(row.sort_order ?? row.sortOrder, fallbackSortOrder),
});

const normalizeWalletTransaction = (row = {}) => ({
  id: toSafeText(row.id, 80),
  companyId: toSafeText(row.company_id, 80),
  direction: toSafeText(row.direction, 20) || "credit",
  transactionType:
    toSafeText(row.transaction_type || row.transactionType, 40) || "manual_credit",
  amountUnits: toSafeLong(row.amount_units ?? row.amountUnits, 0),
  balanceBeforeUnits: toSafeLong(
    row.balance_before_units ?? row.balanceBeforeUnits,
    0
  ),
  balanceAfterUnits: toSafeLong(row.balance_after_units ?? row.balanceAfterUnits, 0),
  note: toSafeText(row.note, 280),
  createdBy: toSafeText(row.created_by || row.createdBy, 80),
  createdAt: toSafeText(row.created_at || row.createdAt, 80),
});

const normalizeUsageEvent = (row = {}) => ({
  id: toSafeText(row.id, 80),
  companyId: toSafeText(row.company_id, 80),
  branchId: toSafeText(row.branch_id, 80),
  eventKey: toSafeText(row.event_key || row.eventKey, 80),
  eventLabel: toSafeText(row.event_label || row.eventLabel, 120),
  unitsConsumed: toSafeLong(row.units_consumed ?? row.unitsConsumed, 0),
  sourceChannel: toSafeText(row.source_channel || row.sourceChannel, 40),
  sourceMode: toSafeText(row.source_mode || row.sourceMode, 40),
  status: toSafeText(row.status, 30) || "recorded",
  orderId: toSafeText(row.order_id || row.orderId, 120),
  createdAt: toSafeText(row.created_at || row.createdAt, 80),
});

const ensurePlatformSettingsRow = async () => {
  const { data, error } = await supabase
    .from("platform_billing_settings")
    .select("*")
    .eq("singleton_key", DEFAULT_PLATFORM_BILLING_SETTINGS.singletonKey)
    .maybeSingle();

  if (error) {
    if (isMissingMzBillingStructureError(error)) {
      throw new Error(
        "Falta aplicar platform_wallet_mz_phase1_patch.sql en Supabase para habilitar la billetera MZ."
      );
    }

    throw normalizeError(
      error,
      "No se pudo leer la configuracion global de la billetera MZ."
    );
  }

  if (data) {
    return normalizePlatformBillingSettings(data);
  }

  const insertPayload = {
    singleton_key: DEFAULT_PLATFORM_BILLING_SETTINGS.singletonKey,
    unit_code: DEFAULT_PLATFORM_BILLING_SETTINGS.unitCode,
    unit_singular: DEFAULT_PLATFORM_BILLING_SETTINGS.unitSingular,
    unit_plural: DEFAULT_PLATFORM_BILLING_SETTINGS.unitPlural,
    default_low_balance_threshold:
      DEFAULT_PLATFORM_BILLING_SETTINGS.defaultLowBalanceThreshold,
    default_negative_limit: DEFAULT_PLATFORM_BILLING_SETTINGS.defaultNegativeLimit,
  };

  const { data: inserted, error: insertError } = await supabase
    .from("platform_billing_settings")
    .insert(insertPayload)
    .select("*")
    .single();

  if (insertError) {
    throw normalizeError(
      insertError,
      "No se pudo crear la configuracion base de la billetera MZ."
    );
  }

  return normalizePlatformBillingSettings(inserted);
};

const ensurePlatformPricingRules = async () => {
  const { count, error } = await supabase
    .from("platform_pricing_rules")
    .select("id", { count: "exact", head: true });

  if (error) {
    if (isMissingMzBillingStructureError(error)) {
      throw new Error(
        "Falta aplicar platform_wallet_mz_phase1_patch.sql en Supabase para habilitar las reglas MZ."
      );
    }

    throw normalizeError(error, "No se pudo validar las reglas de consumo MZ.");
  }

  if (Number(count || 0) > 0) {
    return;
  }

  const insertPayload = DEFAULT_PLATFORM_PRICING_RULES.map((rule) => ({
    event_key: rule.eventKey,
    event_label: rule.eventLabel,
    description: rule.description,
    units_cost: rule.unitsCost,
    enabled: rule.enabled,
    category: rule.category,
    sort_order: rule.sortOrder,
  }));

  const { error: insertError } = await supabase
    .from("platform_pricing_rules")
    .insert(insertPayload);

  if (insertError) {
    throw normalizeError(
      insertError,
      "No se pudieron sembrar las reglas base de consumo MZ."
    );
  }
};

const ensurePlatformCreditPackages = async () => {
  const { data: currentPackages, error } = await supabase
    .from("platform_credit_packages")
    .select("package_code, enabled");

  if (error) {
    if (isMissingMzBillingStructureError(error)) {
      throw new Error(
        "Falta aplicar platform_wallet_mz_phase1_patch.sql en Supabase para habilitar los paquetes MZ."
      );
    }

    throw normalizeError(error, "No se pudo validar el catalogo de paquetes MZ.");
  }

  const currentCodes = new Set(
    (currentPackages || []).map((pkg) => toSafeText(pkg.package_code, 80))
  );
  const hasRecommendedPackage = DEFAULT_PLATFORM_CREDIT_PACKAGES.some((pkg) =>
    currentCodes.has(pkg.packageCode)
  );
  const hasOnlyLegacyPackages =
    currentCodes.size > 0 &&
    !hasRecommendedPackage &&
    Array.from(currentCodes).every((packageCode) =>
      LEGACY_PLATFORM_CREDIT_PACKAGE_CODES.has(packageCode)
    );

  if (currentCodes.size > 0 && !hasOnlyLegacyPackages) {
    return;
  }

  const insertPayload = DEFAULT_PLATFORM_CREDIT_PACKAGES.map((pkg) => ({
    package_code: pkg.packageCode,
    package_name: pkg.packageName,
    description: pkg.description,
    units_amount: pkg.unitsAmount,
    price_ars: pkg.priceArs,
    enabled: pkg.enabled,
    sort_order: pkg.sortOrder,
  }));

  const { error: insertError } = await supabase
    .from("platform_credit_packages")
    .upsert(insertPayload, { onConflict: "package_code" });

  if (insertError) {
    throw normalizeError(
      insertError,
      "No se pudieron sembrar los paquetes base de la billetera MZ."
    );
  }

  if (hasOnlyLegacyPackages) {
    const disableResults = await Promise.all(
      Array.from(LEGACY_PLATFORM_CREDIT_PACKAGE_CODES).map((packageCode) =>
        supabase
          .from("platform_credit_packages")
          .update({ enabled: false })
          .eq("package_code", packageCode)
      )
    );
    const firstDisableError = disableResults.find((result) => result.error)?.error;

    if (firstDisableError) {
      throw normalizeError(
        firstDisableError,
        "No se pudieron ocultar los paquetes MZ viejos."
      );
    }
  }
};

const ensureCompanyWalletRow = async (companyId, platformSettings) => {
  if (!companyId) {
    return buildDefaultCompanyWallet("");
  }

  const { data, error } = await supabase
    .from("company_wallets")
    .select("*")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    if (isMissingMzBillingStructureError(error)) {
      throw new Error(
        "Falta aplicar platform_wallet_mz_phase1_patch.sql en Supabase para habilitar la billetera por empresa."
      );
    }

    throw normalizeError(error, "No se pudo leer la billetera MZ de la empresa.");
  }

  if (data) {
    const normalizedWallet = normalizeCompanyWallet(data, platformSettings);

    if (
      normalizedWallet.balanceUnits <= 0 &&
      !normalizedWallet.lastTransactionAt
    ) {
      const { data: bootstrappedWallet, error: bootstrapError } = await supabase
        .from("company_wallets")
        .update({
          balance_units: MANUAL_OPERATIONAL_MZ_RESET_UNITS,
        })
        .eq("company_id", companyId)
        .select("*")
        .single();

      if (bootstrapError) {
        throw normalizeError(
          bootstrapError,
          "No se pudo restaurar el cupo operativo base de la empresa."
        );
      }

      return normalizeCompanyWallet(bootstrappedWallet, platformSettings);
    }

    return normalizedWallet;
  }

  const insertPayload = {
    company_id: companyId,
    balance_units: MANUAL_OPERATIONAL_MZ_RESET_UNITS,
    low_balance_threshold:
      platformSettings.defaultLowBalanceThreshold ??
      DEFAULT_PLATFORM_BILLING_SETTINGS.defaultLowBalanceThreshold,
    allow_negative_balance: false,
    negative_balance_limit:
      platformSettings.defaultNegativeLimit ??
      DEFAULT_PLATFORM_BILLING_SETTINGS.defaultNegativeLimit,
  };

  const { data: inserted, error: insertError } = await supabase
    .from("company_wallets")
    .insert(insertPayload)
    .select("*")
    .single();

  if (insertError) {
    throw normalizeError(
      insertError,
      "No se pudo crear la billetera base de la empresa."
    );
  }

  return normalizeCompanyWallet(inserted, platformSettings);
};

export const getMzBackofficeSnapshot = async (companyId) => {
  ensureSupabaseReady();

  const platformSettings = await ensurePlatformSettingsRow();
  await Promise.all([ensurePlatformPricingRules(), ensurePlatformCreditPackages()]);
  const companyWallet = await ensureCompanyWalletRow(companyId, platformSettings);

  const [
    pricingRulesResult,
    creditPackagesResult,
    recentTransactionsResult,
    recentUsageEventsResult,
  ] = await Promise.all([
    supabase
      .from("platform_pricing_rules")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true }),
    supabase
      .from("platform_credit_packages")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true }),
    companyId
      ? supabase
          .from("company_wallet_transactions")
          .select("*")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false })
          .limit(8)
      : Promise.resolve({ data: [], error: null }),
    companyId
      ? supabase
          .from("company_usage_events")
          .select("*")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false })
          .limit(8)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (pricingRulesResult.error) {
    throw normalizeError(
      pricingRulesResult.error,
      "No se pudieron cargar las reglas de consumo MZ."
    );
  }

  if (creditPackagesResult.error) {
    throw normalizeError(
      creditPackagesResult.error,
      "No se pudieron cargar los paquetes de saldo MZ."
    );
  }

  if (recentTransactionsResult.error) {
    throw normalizeError(
      recentTransactionsResult.error,
      "No se pudo cargar el historial de movimientos MZ de la empresa."
    );
  }

  if (recentUsageEventsResult.error) {
    throw normalizeError(
      recentUsageEventsResult.error,
      "No se pudo cargar el historial de consumo MZ."
    );
  }

  return {
    platformSettings,
    companyWallet,
    pricingRules: (pricingRulesResult.data || []).map((rule, index) =>
      normalizePricingRule(rule, (index + 1) * 10)
    ),
    creditPackages: (creditPackagesResult.data || []).map((pkg, index) =>
      normalizeCreditPackage(pkg, (index + 1) * 10)
    ),
    recentTransactions: (recentTransactionsResult.data || []).map(
      normalizeWalletTransaction
    ),
    recentUsageEvents: (recentUsageEventsResult.data || []).map(normalizeUsageEvent),
  };
};

export const updatePlatformBillingSettings = async (payload = {}) => {
  ensureSupabaseReady();

  const currentSettings = await ensurePlatformSettingsRow();
  const nextSettings = normalizePlatformBillingSettings({
    singletonKey: currentSettings.singletonKey,
    unitCode: payload.unitCode ?? currentSettings.unitCode,
    unitSingular: payload.unitSingular ?? currentSettings.unitSingular,
    unitPlural: payload.unitPlural ?? currentSettings.unitPlural,
    defaultLowBalanceThreshold:
      payload.defaultLowBalanceThreshold ?? currentSettings.defaultLowBalanceThreshold,
    defaultNegativeLimit:
      payload.defaultNegativeLimit ?? currentSettings.defaultNegativeLimit,
  });

  const { data, error } = await supabase
    .from("platform_billing_settings")
    .upsert({
      singleton_key: nextSettings.singletonKey,
      unit_code: nextSettings.unitCode,
      unit_singular: nextSettings.unitSingular,
      unit_plural: nextSettings.unitPlural,
      default_low_balance_threshold: nextSettings.defaultLowBalanceThreshold,
      default_negative_limit: nextSettings.defaultNegativeLimit,
    })
    .select("*")
    .single();

  if (error) {
    throw normalizeError(
      error,
      "No se pudo guardar la configuracion global de la billetera MZ."
    );
  }

  return normalizePlatformBillingSettings(data);
};

export const updateCompanyWalletSettings = async (companyId, payload = {}) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para configurar la billetera.");
  }

  const platformSettings = await ensurePlatformSettingsRow();
  const currentWallet = await ensureCompanyWalletRow(companyId, platformSettings);
  const nextWallet = normalizeCompanyWallet(
    {
      companyId,
      balanceUnits: currentWallet.balanceUnits,
      lowBalanceThreshold:
        payload.lowBalanceThreshold ?? currentWallet.lowBalanceThreshold,
      allowNegativeBalance:
        payload.allowNegativeBalance ?? currentWallet.allowNegativeBalance,
      negativeBalanceLimit:
        payload.negativeBalanceLimit ?? currentWallet.negativeBalanceLimit,
      lastTransactionAt: currentWallet.lastTransactionAt,
    },
    platformSettings
  );

  const { data, error } = await supabase
    .from("company_wallets")
    .upsert({
      company_id: companyId,
      balance_units: currentWallet.balanceUnits,
      low_balance_threshold: nextWallet.lowBalanceThreshold,
      allow_negative_balance: nextWallet.allowNegativeBalance,
      negative_balance_limit: nextWallet.negativeBalanceLimit,
      last_transaction_at: currentWallet.lastTransactionAt || null,
    })
    .select("*")
    .single();

  if (error) {
    throw normalizeError(
      error,
      "No se pudo guardar la configuracion operativa de la billetera."
    );
  }

  return normalizeCompanyWallet(data, platformSettings);
};

export const applyCompanyWalletManualAdjustment = async (
  companyId,
  { direction = "credit", amountUnits = 0, note = "", actorUserId = "" } = {}
) => {
  ensureSupabaseReady();

  if (!companyId) {
    throw new Error("No encontramos una empresa activa para mover saldo.");
  }

  const normalizedDirection =
    toSafeText(direction, 20).toLowerCase() === "debit" ? "debit" : "credit";
  const normalizedAmountUnits = toSafeLong(amountUnits, 0);
  const normalizedNote = toSafeText(note, 280);

  if (normalizedAmountUnits <= 0) {
    throw new Error("Carga una cantidad de MZ mayor a cero para mover saldo.");
  }

  const platformSettings = await ensurePlatformSettingsRow();
  const currentWallet = await ensureCompanyWalletRow(companyId, platformSettings);
  const signedDelta =
    normalizedDirection === "debit" ? -normalizedAmountUnits : normalizedAmountUnits;
  const nextBalanceUnits = currentWallet.balanceUnits + signedDelta;
  const negativeFloor = currentWallet.allowNegativeBalance
    ? -currentWallet.negativeBalanceLimit
    : 0;

  if (nextBalanceUnits < negativeFloor) {
    throw new Error(
      currentWallet.allowNegativeBalance
        ? "La operacion deja la billetera por debajo del limite negativo permitido."
        : "La empresa no tiene saldo suficiente y la billetera no admite saldo negativo."
    );
  }

  const timestamp = new Date().toISOString();
  const transactionPayload = {
    company_id: companyId,
    direction: normalizedDirection,
    transaction_type:
      normalizedDirection === "debit" ? "manual_debit" : "manual_credit",
    amount_units: normalizedAmountUnits,
    balance_before_units: currentWallet.balanceUnits,
    balance_after_units: nextBalanceUnits,
    note: normalizedNote || "Ajuste manual desde Backoffice.",
    created_by: toSafeText(actorUserId, 80) || null,
    metadata: {
      source: "backoffice_manual_adjustment",
      at: timestamp,
    },
  };

  const { data: updatedWallet, error: updateWalletError } = await supabase
    .from("company_wallets")
    .update({
      balance_units: nextBalanceUnits,
      last_transaction_at: timestamp,
    })
    .eq("company_id", companyId)
    .select("*")
    .single();

  if (updateWalletError) {
    throw normalizeError(
      updateWalletError,
      "No se pudo actualizar el saldo de la billetera."
    );
  }

  const { data: insertedTransaction, error: insertTransactionError } = await supabase
    .from("company_wallet_transactions")
    .insert(transactionPayload)
    .select("*")
    .single();

  if (insertTransactionError) {
    await supabase
      .from("company_wallets")
      .update({
        balance_units: currentWallet.balanceUnits,
        last_transaction_at: currentWallet.lastTransactionAt || null,
      })
      .eq("company_id", companyId);

    throw normalizeError(
      insertTransactionError,
      "No se pudo registrar el movimiento manual del saldo."
    );
  }

  return {
    companyWallet: normalizeCompanyWallet(updatedWallet, platformSettings),
    transaction: normalizeWalletTransaction(insertedTransaction),
  };
};

export const updatePlatformPricingRules = async (rules = []) => {
  ensureSupabaseReady();

  const normalizedRules = (Array.isArray(rules) ? rules : [])
    .map((rule, index) =>
      normalizePricingRule(
        {
          ...rule,
          sortOrder: rule?.sortOrder ?? (index + 1) * 10,
        },
        (index + 1) * 10
      )
    )
    .filter((rule) => rule.eventKey && rule.eventLabel);

  if (normalizedRules.length === 0) {
    throw new Error("No encontramos reglas MZ validas para guardar.");
  }

  const payload = normalizedRules.map((rule) => ({
    event_key: rule.eventKey,
    event_label: rule.eventLabel,
    description: rule.description,
    units_cost: rule.unitsCost,
    enabled: rule.enabled,
    category: rule.category,
    sort_order: rule.sortOrder,
  }));

  const { data, error } = await supabase
    .from("platform_pricing_rules")
    .upsert(payload, { onConflict: "event_key" })
    .select("*");

  if (error) {
    throw normalizeError(error, "No se pudieron guardar las reglas de consumo MZ.");
  }

  return (data || [])
    .map((rule, index) => normalizePricingRule(rule, (index + 1) * 10))
    .sort((left, right) => left.sortOrder - right.sortOrder);
};

export const updatePlatformCreditPackages = async (packages = []) => {
  ensureSupabaseReady();

  const normalizedPackages = (Array.isArray(packages) ? packages : [])
    .map((pkg, index) =>
      normalizeCreditPackage(
        {
          ...pkg,
          sortOrder: pkg?.sortOrder ?? (index + 1) * 10,
        },
        (index + 1) * 10
      )
    )
    .filter((pkg) => pkg.packageCode && pkg.packageName);

  if (normalizedPackages.length === 0) {
    throw new Error("No encontramos paquetes MZ validos para guardar.");
  }

  const payload = normalizedPackages.map((pkg) => ({
    package_code: pkg.packageCode,
    package_name: pkg.packageName,
    description: pkg.description,
    units_amount: pkg.unitsAmount,
    price_ars: pkg.priceArs,
    enabled: pkg.enabled,
    sort_order: pkg.sortOrder,
  }));
  const activePackageCodes = new Set(
    normalizedPackages.map((pkg) => pkg.packageCode)
  );

  const { data, error } = await supabase
    .from("platform_credit_packages")
    .upsert(payload, { onConflict: "package_code" })
    .select("*");

  if (error) {
    throw normalizeError(error, "No se pudieron guardar los paquetes MZ.");
  }

  const { data: currentRows, error: currentRowsError } = await supabase
    .from("platform_credit_packages")
    .select("*");

  if (currentRowsError) {
    throw normalizeError(
      currentRowsError,
      "No se pudo revisar el catalogo completo de paquetes MZ."
    );
  }

  const staleRows = (currentRows || [])
    .map((row) => normalizeCreditPackage(row))
    .filter((pkg) => pkg.enabled && !activePackageCodes.has(pkg.packageCode));

  if (staleRows.length > 0) {
    const disableResults = await Promise.all(
      staleRows.map((pkg) =>
        supabase
          .from("platform_credit_packages")
          .update({ enabled: false })
          .eq("package_code", pkg.packageCode)
      )
    );
    const firstDisableError = disableResults.find((result) => result.error)?.error;

    if (firstDisableError) {
      throw normalizeError(
        firstDisableError,
        "No se pudieron ocultar los paquetes MZ removidos."
      );
    }
  }

  const { data: finalRows, error: finalRowsError } = await supabase
    .from("platform_credit_packages")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (finalRowsError) {
    throw normalizeError(
      finalRowsError,
      "No se pudo recargar el catalogo actualizado de paquetes MZ."
    );
  }

  return (finalRows || data || [])
    .map((pkg, index) => normalizeCreditPackage(pkg, (index + 1) * 10))
    .sort((left, right) => left.sortOrder - right.sortOrder);
};
